import React, { useState, useEffect } from 'react';
import './VisitorsList.css';
import { getCurrentVisitors, checkoutVisitor } from '../../services/api';
import { formatDateTime } from '../../utils/dateHelpers';

// Cookie helper functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

function VisitorsList() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Initialize from cookie, default to 'card'
    return getCookie('visitorsViewMode') || 'card';
  });

  const fetchVisitors = async () => {
    try {
      const data = await getCurrentVisitors();
      // Sort: overnight visitors first, then by check-in time (newest first)
      const sorted = data.sort((a, b) => {
        // Overnight visitors come first
        if (a.is_overnight && !b.is_overnight) return -1;
        if (!a.is_overnight && b.is_overnight) return 1;
        // Within same category, sort by check-in time (newest first)
        return new Date(b.check_in_time) - new Date(a.check_in_time);
      });
      setVisitors(sorted);
      setError('');
    } catch (err) {
      setError('Kunde inte hämta besökare. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitors();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchVisitors, 30000);

    return () => clearInterval(interval);
  }, []);

  const toggleViewMode = () => {
    const newMode = viewMode === 'card' ? 'table' : 'card';
    setViewMode(newMode);
    setCookie('visitorsViewMode', newMode);
  };

  const handleCheckout = async (visitor) => {
    if (!window.confirm(`Vill du checka ut ${visitor.name}?`)) {
      return;
    }

    try {
      await checkoutVisitor(visitor.id);
      // Refresh list after checkout
      fetchVisitors();
    } catch (err) {
      alert('Kunde inte checka ut besökaren. Försök igen.');
    }
  };

  if (loading) {
    return <div className="loading">Laddar besökare...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchVisitors} className="retry-button">
          Försök igen
        </button>
      </div>
    );
  }

  if (visitors.length === 0) {
    return (
      <div className="empty-state">
        <h2>Inga nuvarande besökare</h2>
        <p>Det finns inga incheckade besökare just nu.</p>
      </div>
    );
  }

  return (
    <div className="visitors-container">
      <div className="visitors-header">
        <h2>Nuvarande besökare ({visitors.length})</h2>
        <button onClick={toggleViewMode} className="view-toggle-button">
          {viewMode === 'card' ? 'Visa tabell' : 'Visa kort'}
        </button>
      </div>
      <p className="visitors-instruction">
        {viewMode === 'card' ? 'Klicka på ett kort för att checka ut' : 'Klicka på en rad för att checka ut'}
      </p>

      {viewMode === 'card' ? (
        <div className="visitors-grid">
          {visitors.map(visitor => (
            <div
              key={visitor.id}
              className={`visitor-card ${visitor.is_overnight ? 'overnight' : ''}`}
              onClick={() => handleCheckout(visitor)}
            >
              <div className="visitor-header">
                <h3>{visitor.name}</h3>
              </div>
              <div className="visitor-info">
                <p><strong>Företag:</strong> {visitor.company}</p>
                <p><strong>Ärende:</strong> {visitor.reason}</p>
                <p><strong>Telefon:</strong> {visitor.phone}</p>
                <p><strong>Incheckning:</strong> {formatDateTime(visitor.check_in_time)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="visitors-table-wrapper">
          <table className="visitors-table">
            <thead>
              <tr>
                <th>Namn</th>
                <th>Företag</th>
                <th>Ärende</th>
                <th>Telefon</th>
                <th>Incheckning</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map(visitor => (
                <tr
                  key={visitor.id}
                  className={`visitor-row ${visitor.is_overnight ? 'overnight-row' : ''}`}
                  onClick={() => handleCheckout(visitor)}
                >
                  <td>
                    <div className="table-name-cell">
                      {visitor.name}
                      {visitor.is_overnight && (
                        <span className="overnight-badge-small">Övernattande</span>
                      )}
                    </div>
                  </td>
                  <td>{visitor.company}</td>
                  <td>{visitor.reason}</td>
                  <td>{visitor.phone}</td>
                  <td>{formatDateTime(visitor.check_in_time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default VisitorsList;